/**
 * HTTP client utilities for REST API requests.
 * Provides standardized GET/POST wrappers with abort signal and timeout support.
 */

import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosRequestHeaders,
  type AxiosResponse,
} from 'axios';

import type { RestRequestOptions } from './types';

/**
 * Resolves the axios instance that should perform the request.
 */
export const resolveClient = (options?: RestRequestOptions): AxiosInstance =>
  options?.client ?? axios;

interface RequestArgs<TParams> {
  url: string;
  params?: TParams;
  options?: RestRequestOptions;
}

interface PostArgs<TBody, TParams> extends RequestArgs<TParams> {
  body?: TBody;
}

/**
 * Utility wrapper that standardises GET requests with optional AbortSignal
 * and timeout support across all exchanges.
 */
export const get = async <TResponse, TParams = Record<string, unknown>>({
  url,
  params,
  options,
}: RequestArgs<TParams>): Promise<AxiosResponse<TResponse>> => {
  const client = resolveClient(options);
  const config: AxiosRequestConfig<TParams> = {
    params,
    signal: options?.signal,
    timeout: options?.timeoutMs,
    headers: options?.headers,
  };

  return client.get<TResponse>(url, config);
};

/**
 * Standardised POST helper mirroring the optional AbortSignal/headers logic.
 */
export const post = async <TResponse, TBody = unknown, TParams = Record<string, unknown>>({
  url,
  body,
  params,
  options,
}: PostArgs<TBody, TParams>): Promise<AxiosResponse<TResponse>> => {
  const client = resolveClient(options);
  const config: AxiosRequestConfig<TParams> = {
    params,
    signal: options?.signal,
    timeout: options?.timeoutMs,
    headers: options?.headers as AxiosRequestHeaders | undefined,
  };

  return client.post<TResponse>(url, body, config);
};
